// Importación de módulos y librerías necesarias
import React, { useEffect, useState, useCallback } from "react"; // Hooks de React
import { FaEdit, FaPlus } from "react-icons/fa"; // Íconos para botones
import axios from "axios"; // Cliente HTTP
import md5 from "md5"; // Para encriptar contraseñas
import "../../css/ConsultaEnvio.css"; // Estilos personalizados
import Swal from "sweetalert2";
import Cookies from "universal-cookie";
import { Navigate } from "react-router-dom";

// Componente principal para gestión de usuarios
const Usuarios = () => {
  const cookies = new Cookies();
  const [userName, setUserName] = useState("");
  const [rol, setRol] = useState("");

  // Estados para filtros y control general
  const [busqueda, setBusqueda] = useState(""); // Filtro por texto (usuario o correo)
  const [estado, setEstado] = useState(""); // Filtro por estado (Activo/Inactivo)

  // Lista filtrada de usuarios mostrada en la tabla
  const [datosFiltrados, setDatosFiltrados] = useState([]);

  // Control de paginación
  const [paginaActual, setPaginaActual] = useState(1);

  // Control de modal y edición
  const [modalVisible, setModalVisible] = useState(false); // Modal abierto/cerrado
  const [modoEdicion, setModoEdicion] = useState(false); // Indica si es edición
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null); // Usuario seleccionado para edición

  const [modalContrasenaVisible, setModalContrasenaVisible] = useState(false);
  const [usuarioCambioContrasena, setUsuarioCambioContrasena] = useState(null);
  const [nuevaContrasena, setNuevaContrasena] = useState("");
  const [verNuevaContrasena, setVerNuevaContrasena] = useState(false);

  // Modelo de formulario (vinculado a inputs del modal)
  const [formulario, setFormulario] = useState({
    nombreUsuario: "",
    nombre: "",
    apellido: "",
    correo: "",
    contrasena: "",
    estado: true, // true: Activo, false: Inactivo
    rol: "Estandar",
    usuCrea: userName,
    usuMod: userName,
  });

  const elementosPorPagina = 10; // Cantidad de filas por página
  const baseURL = process.env.REACT_APP_API_BASE_URL; // URL base desde archivo .env

  // Función para obtener usuarios desde la API y aplicar filtros
  const fetchUsuarios = useCallback(async () => {
    try {
      const response = await axios.get(
        `${baseURL}/api/Usuarios/ObtenerTodosUsuarios`
      );
      const data = response.data;

      // Transformación del formato de los usuarios
      const transformado = data.map((item) => ({
        nombreUsuario: item.nombreUsuario,
        nombre: item.nombre,
        apellido: item.apellido,
        correo: item.correo,
        estado: item.estado === "A" ? "Activo" : "Inactivo",
        rol: item.rol ?? "Usuario",
      }));

      // Filtro según búsqueda y estado
      const filtrados = transformado.filter((item) => {
        const coincideBusqueda =
          item.nombreUsuario.toLowerCase().includes(busqueda.toLowerCase()) ||
          item.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
          item.apellido.toLowerCase().includes(busqueda.toLowerCase()) ||
          item.correo.toLowerCase().includes(busqueda.toLowerCase());

        const coincideEstado = estado === "" || item.estado === estado;
        return coincideBusqueda && coincideEstado;
      });

      setDatosFiltrados(filtrados);
      setPaginaActual(1); // Reiniciar a página 1 después de filtrar
    } catch (error) {
      console.error("Error al obtener usuarios:", error);
      Swal.fire({
        icon: "error",
        title: "Error al cargar usuarios",
        text: "Ocurrió un problema al obtener la lista de usuarios. Intenta nuevamente.",
      });
    }
  }, [baseURL, busqueda, estado]);

  // Ejecutar fetchUsuarios cuando se monta el componente o cambian filtros
  useEffect(() => {
    setUserName(cookies.get("nombreUsuario") || "");
    setRol(cookies.get("rol") || "");

    fetchUsuarios();
  }, [fetchUsuarios]);

  // Abre el modal en modo creación (limpia formulario)
  const abrirModalAgregar = () => {
    setModoEdicion(false);
    setFormulario({
      nombreUsuario: "",
      nombre: "",
      apellido: "",
      correo: "",
      contrasena: "",
      estado: true,
      rol: "Estandar",
      usuCrea: userName,
      usuMod: userName,
    });
    setModalVisible(true);
  };

  // Abre el modal en modo edición, cargando los datos del usuario seleccionado
  const abrirModalEditar = (usuario) => {
    setModoEdicion(true);
    setFormulario({
      nombreUsuario: usuario.nombreUsuario,
      nombre: usuario.nombre,
      apellido: usuario.apellido,
      correo: usuario.correo,
      contrasena: "",
      estado: usuario.estado === "Activo",
      rol: usuario.rol,
      usuCrea: userName,
      usuMod: userName,
    });
    setUsuarioSeleccionado(usuario.nombreUsuario);
    setModalVisible(true);
  };

  // Manejador para cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormulario({
      ...formulario,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Función para guardar un nuevo usuario o actualizar uno existente
  const guardarUsuario = async () => {
    // Validaciones básicas
    if (!formulario.nombreUsuario || !formulario.nombre || !formulario.correo) {
      Swal.fire({
        toast: true,
        icon: "warning",
        title: "Todos los campos obligatorios deben estar completos.",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    if (!modoEdicion && !formulario.contrasena) {
      Swal.fire({
        toast: true,
        icon: "warning",
        title: "La contraseña es obligatoria.",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    // Validar si el nombre de usuario ya existe (solo en modo creación)
    if (!modoEdicion) {
      const yaExiste = datosFiltrados.some(
        (u) =>
          u.nombreUsuario.toLowerCase() ===
          formulario.nombreUsuario.toLowerCase()
      );

      if (yaExiste) {
        Swal.fire({
          toast: true,
          icon: "error",
          title: "El nombre de usuario ya está registrado.",
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
        return;
      }
    }

    // Crear payload, transformando contraseña y estado
    const payload = {
      ...formulario,
      contrasena: formulario.contrasena
        ? md5(formulario.contrasena)
        : undefined,
      estado: formulario.estado ? "A" : "I",
    };

    try {
      if (modoEdicion) {
        await axios.post(`${baseURL}/api/Usuarios/ActualizarUsuario`, payload);

        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Usuario actualizado correctamente.",
        });
      } else {
        await axios.post(`${baseURL}/api/Usuarios/CrearUsuario`, payload);
        Swal.fire({
          icon: "success",
          title: "Éxito",
          text: "Usuario creado correctamente.",
        });
      }
      setModalVisible(false); // Cierra modal
      setUsuarioSeleccionado(null); // Limpia selección
      fetchUsuarios(); // Refresca datos
    } catch (error) {
      console.error("Error al guardar:", error);
      alert("❌ Error al guardar el usuario.");
      Swal.fire("Error", "Error al guardar:" + error, "error");
    }
  };

  const abrirModalCambioContrasena = (usuario) => {
    setUsuarioCambioContrasena(usuario);
    setUsuarioSeleccionado(usuario.nombreUsuario);
    setNuevaContrasena("");
    setModalContrasenaVisible(true);
  };

  const guardarNuevaContrasena = async () => {
    if (!nuevaContrasena) {
      Swal.fire({
        toast: true,
        icon: "warning",
        title: "Debes ingresar la nueva contraseña.",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    }

    try {
      await axios.post(`${baseURL}/api/Usuarios/CambiarContrasena`, {
        nombreUsuario: usuarioCambioContrasena.nombreUsuario,
        contrasena: md5(nuevaContrasena),
      });

      Swal.fire({
        toast: true,
        icon: "success",
        title: "Contraseña actualizada correctamente.",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      setModalContrasenaVisible(false);
      setUsuarioSeleccionado();
    } catch (error) {
      console.error("Error al cambiar contraseña:", error);
      Swal.fire({
        toast: true,
        icon: "error",
        title: "No se pudo cambiar la contraseña.",
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  // Cálculo de rangos para paginación
  const indexInicio = (paginaActual - 1) * elementosPorPagina;
  const indexFin = indexInicio + elementosPorPagina;
  const datosPaginados = datosFiltrados.slice(indexInicio, indexFin);
  const totalPaginas = Math.ceil(datosFiltrados.length / elementosPorPagina);

  //SI el usuario logueado no es rol ADMIN lo redirecciona
  if (rol && rol !== "Admin") {
    return <Navigate to="/inicio" />;
  }

  // Render principal del componente
  return (
    <div className="content">
      <div className="container-fluid">
        <div className="card">
          <div className="card-header text-white">
            <h3 className="card-title titulo">📋 Gestión de usuarios</h3>
          </div>

          <div className="card-body">
            {/* Filtros de búsqueda y estado */}
            <div className="row align-items-end">
              <div className="col-md-4">
                <label>🔍 Buscar</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar en toda la tabla"
                  autoComplete="off"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>

              <div className="col-md-2">
                <label>📦 Estado</label>
                <select
                  className="form-control"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="Activo">Activo</option>
                  <option value="Inactivo">Inactivo</option>
                </select>
              </div>

              {/* Botón para abrir modal de nuevo usuario */}
              <div className="col-md-5 text-left"></div>
              <div className="col-md-1 text-left">
                <button
                  className="btn btn-cargar-xml"
                  onClick={abrirModalAgregar}
                >
                  <FaPlus /> Agregar
                </button>
              </div>
            </div>

            {/* Tabla de usuarios */}
            <div className="table-responsive mt-3">
              <table className="table table-bordered table-hover table-no-vertical-borders">
                <thead
                  className="text-white"
                  style={{ backgroundColor: "#003F88" }}
                >
                  <tr>
                    <th>Usuario</th>
                    <th>Nombre</th>
                    <th>Apellido</th>
                    <th>Correo</th>
                    <th>Estado</th>
                    <th>Rol</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {datosPaginados.length > 0 ? (
                    datosPaginados.map((item, index) => (
                      <tr
                        key={index}
                        className={
                          usuarioSeleccionado === item.nombreUsuario
                            ? "table-secondary"
                            : ""
                        }
                      >
                        <td>{item.nombreUsuario}</td>
                        <td>{item.nombre}</td>
                        <td>{item.apellido}</td>
                        <td>{item.correo}</td>
                        <td>
                          <span
                            className={`badge ${
                              item.estado === "Activo"
                                ? "badge-success"
                                : "badge-secondary"
                            }`}
                          >
                            {item.estado}
                          </span>
                        </td>
                        <td>{item.rol}</td>
                        <td>
                          <div className="d-flex flex-wrap">
                            <button
                              className="btn btn-accion mr-1"
                              onClick={() => abrirModalEditar(item)}
                            >
                              <FaEdit /> Editar
                            </button>

                            <button
                              className="btn btn-accion mr-1"
                              onClick={() => abrirModalCambioContrasena(item)}
                            >
                              <FaEdit /> Cambiar Contraseña
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No se encontraron resultados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {totalPaginas > 1 && (
              <div className="mt-3">
                {Array.from({ length: totalPaginas }).map((_, index) => (
                  <button
                    key={index}
                    className={`btn btn-sm ${
                      paginaActual === index + 1
                        ? "btn-secondary"
                        : "btn-outline-secondary"
                    } mr-1`}
                    onClick={() => setPaginaActual(index + 1)}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal para crear/editar usuario */}
        {modalVisible && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                {/* Título del modal */}
                <div
                  className="modal-header text-white"
                  style={{ backgroundColor: "#003F88" }}
                >
                  <h5 className="modal-title">
                    {modoEdicion ? "✏️ Editar Usuario" : "➕ Nuevo Usuario"}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setModalVisible(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>

                {/* Formulario */}
                <div className="modal-body">
                  <form>
                    {/* Campos del formulario */}
                    <div className="form-group">
                      <label>
                        Nombre de usuario<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        name="nombreUsuario"
                        value={formulario.nombreUsuario}
                        onChange={handleChange}
                        disabled={modoEdicion}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Nombre<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        name="nombre"
                        value={formulario.nombre}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Apellido<span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        name="apellido"
                        value={formulario.apellido}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        Correo<span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        autoComplete="off"
                        name="correo"
                        value={formulario.correo}
                        onChange={handleChange}
                      />
                    </div>

                    {/* Contraseña solo visible en modo creación */}
                    {!modoEdicion && (
                      <div className="form-group">
                        <label>
                          Contraseña<span className="text-danger">*</span>
                        </label>
                        <input
                          type="password"
                          className="form-control"
                          autoComplete="off"
                          name="contrasena"
                          value={formulario.contrasena}
                          onChange={handleChange}
                        />
                      </div>
                    )}

                    <div className="form-group">
                      <label>
                        Rol<span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        name="rol"
                        value={formulario.rol}
                        onChange={handleChange}
                      >
                        <option value="Estandar">Estandar</option>
                        <option value="Admin">Admin</option>
                      </select>
                    </div>

                    <div className="form-group form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        autoComplete="off"
                        name="estado"
                        checked={formulario.estado}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">Activo</label>
                    </div>
                  </form>
                </div>

                {/* Botones del modal */}
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setModalVisible(false);
                      setUsuarioSeleccionado(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={guardarUsuario}>
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {modalContrasenaVisible && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <div
                  className="modal-header text-white"
                  style={{ backgroundColor: "#003F88" }}
                >
                  <h5 className="modal-title">🔒 Cambiar Contraseña</h5>
                  <button
                    type="button"
                    className="close"
                    onClick={() => setModalContrasenaVisible(false)}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body">
                  <label>
                    Nueva Contraseña para{" "}
                    <b>{usuarioCambioContrasena?.nombreUsuario}</b>
                  </label>
                  <div className="input-group">
                    <input
                      type={verNuevaContrasena ? "text" : "password"}
                      className="form-control"
                      autoComplete="off"
                      value={nuevaContrasena}
                      onChange={(e) => setNuevaContrasena(e.target.value)}
                    />
                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() =>
                          setVerNuevaContrasena(!verNuevaContrasena)
                        }
                      >
                        <i
                          className={`fas ${
                            verNuevaContrasena ? "fa-eye-slash" : "fa-eye"
                          }`}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setModalContrasenaVisible(false);
                      setUsuarioSeleccionado(null);
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={guardarNuevaContrasena}
                  >
                    Guardar Contraseña
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Exportación del componente
export default Usuarios;
